import { z } from 'zod';

/** Operator-configurable product areas. Parent entries include their listed children. */
export const profileBlockFeatureSchema = z.enum([
  'daily',
  'leaderboards',
  'solo',
  'casual',
  'competitive',
  'endless',
  'practice',
  'versus',
  'host',
  'join',
]);
export type ProfileBlockFeature = z.infer<typeof profileBlockFeatureSchema>;

export const profileBlockFeaturesSchema = z
  .array(profileBlockFeatureSchema)
  .max(profileBlockFeatureSchema.options.length)
  .refine((features) => new Set(features).size === features.length, 'features must be unique');
export type ProfileBlockFeatures = z.infer<typeof profileBlockFeaturesSchema>;

const PARENTS: Partial<Record<ProfileBlockFeature, ProfileBlockFeature>> = {
  casual: 'solo',
  competitive: 'solo',
  endless: 'solo',
  practice: 'solo',
  host: 'versus',
  join: 'versus',
};

/** NULL restricts the whole site; an explicit empty list restricts nothing. */
export function profileFeatureBlocked(
  blockedFeatures: readonly ProfileBlockFeature[] | null,
  feature: ProfileBlockFeature,
): boolean {
  if (blockedFeatures === null) return true;
  const parent = PARENTS[feature];
  return blockedFeatures.includes(feature) || (parent !== undefined && blockedFeatures.includes(parent));
}

export interface ProfileBlockState {
  readonly blockedUntil: number | null;
  readonly blockedFeatures: readonly ProfileBlockFeature[] | null;
}

/**
 * True while an unexpired block covers `feature`. A null feature asks only whether the whole
 * site is blocked, for screens that belong to no feature.
 */
export function profileRestricted(
  block: ProfileBlockState,
  feature: ProfileBlockFeature | null,
  now = Date.now(),
): boolean {
  if (block.blockedUntil === null || block.blockedUntil <= now) return false;
  if (block.blockedFeatures === null) return true;
  return feature !== null && profileFeatureBlocked(block.blockedFeatures, feature);
}

export function versusBlockFeature(isHost: boolean): ProfileBlockFeature {
  return isHost ? 'host' : 'join';
}
